import { Injector, Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
} from 'rxjs/operators';

import { Message } from '@qtek/shared/models';
import { UserStore } from '../user/user.store';
import { IntegrationsActions } from './integrations.actions';
import { IntegrationsService } from './integrations.service';

function isIntercomTokenValid(token: string, userId: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return (
      typeof payload.exp === 'number' &&
      payload.exp > Date.now() / 1000 &&
      typeof payload.user_id === 'string' &&
      payload.user_id.length > 0 &&
      payload.user_id === userId
    );
  } catch {
    return false;
  }
}

@Injectable()
export class IntegrationsEffects {
  private actions$ = inject(Actions);
  private integrationsService = inject(IntegrationsService);
  private userStore = inject(UserStore);
  private injector = inject(Injector);

  getCalendars$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.getCalendars),
      switchMap(() =>
        this.integrationsService.getAvailableCalendars().pipe(
          map(response =>
            IntegrationsActions.getCalendarsSuccess({ payload: response.res })
          ),
          catchError(({ sts }) =>
            of(IntegrationsActions.getCalendarsFailure({ payload: sts }))
          )
        )
      )
    )
  );

  getCalendar$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.getCalendar),
      switchMap(({ payload }) =>
        this.integrationsService.getVendorCalendar(payload).pipe(
          map(response => {
            if (
              response.sts &&
              response.sts.sts === 'WARN' &&
              response.sts.attrs &&
              response.sts.attrs['google-approval-prompt-required']
            ) {
              return IntegrationsActions.calendarApprovalError({
                payload: { vnd: payload },
              });
            } else if (response.sts && response.sts.sts === 'WARN') {
              return IntegrationsActions.calendarAuthError({ payload });
            } else {
              return IntegrationsActions.getCalendarSuccess({
                payload: response.res,
              });
            }
          }),
          catchError(({ sts }) =>
            of(IntegrationsActions.getCalendarFailure({ payload: sts }))
          )
        )
      )
    )
  );

  getClover$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.getClover),
      switchMap(() =>
        this.integrationsService.getClover().pipe(
          map(response =>
            IntegrationsActions.getCloverSuccess({ payload: response.res })
          ),
          catchError(({ sts }) =>
            of(IntegrationsActions.getCloverFailure({ payload: sts }))
          )
        )
      )
    )
  );

  getSalesforce$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.getSalesforce),
      switchMap(() =>
        this.integrationsService.getSalesforce().pipe(
          map(response =>
            IntegrationsActions.getSalesforceSuccess({ payload: response.res })
          ),
          catchError(({ sts }) =>
            of(IntegrationsActions.getSalesforceFailure({ payload: sts }))
          )
        )
      )
    )
  );

  getMicrosoft$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.getMicrosoft),
      switchMap(() =>
        this.integrationsService.getMicrosoft().pipe(
          map(response =>
            IntegrationsActions.getMicrosoftSuccess({ payload: response.res })
          ),
          catchError(({ sts }) =>
            of(IntegrationsActions.getMicrosoftFailure({ payload: sts }))
          )
        )
      )
    )
  );

  connectCalendar$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.connectCalendar),
      switchMap(({ payload }) =>
        this.integrationsService
          .connectCalendar(payload.vendor, payload.data)
          .pipe(
            map(({ res }) =>
              IntegrationsActions.connectCalendarSuccess({
                payload: res,
                message: new Message('FTR_INTEGRATIONS_CONNECTED'),
              })
            ),
            catchError(({ sts }) =>
              of(IntegrationsActions.connectCalendarFailure({ payload: sts }))
            )
          )
      )
    )
  );

  disconnectCalendar$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.disconnectCalendar),
      switchMap(({ type, ...payload }) =>
        this.integrationsService.disconnectCalendar(payload.payload).pipe(
          map(() =>
            IntegrationsActions.disconnectCalendarSuccess({
              payload: payload.payload,
              message: new Message('FTR_INTEGRATIONS_DISCONNECTED'),
            })
          ),
          catchError(({ sts }) =>
            of(IntegrationsActions.disconnectCalendarFailure({ payload: sts }))
          )
        )
      )
    )
  );

  updateCalendar$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.updateCalendar),
      switchMap(({ payload }) =>
        this.integrationsService
          .updateCalendar(payload.vendor, payload.data)
          .pipe(
            map(response => {
              if (response.sts && response.sts.sts === 'WARN') {
                return IntegrationsActions.calendarAuthError({
                  payload: payload.vendor,
                });
              } else {
                return IntegrationsActions.updateCalendarSuccess({
                  payload: response.res,
                  message: new Message('FTR_INTEGRATIONS_UPDATED'),
                });
              }
            }),
            catchError(({ sts }) =>
              of(IntegrationsActions.updateCalendarFailure({ payload: sts }))
            )
          )
      )
    )
  );

  connectDebtX$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.connectDebtX),
      switchMap(({ payload }) =>
        this.integrationsService.connectDebtX(payload).pipe(
          map(response => {
            if (response.sts && response.sts.sts === 'WARN') {
              return IntegrationsActions.connectDebtXFailure({ payload });
            } else {
              switch (response.res.status) {
                // case 'DEBTX_CONNECTED':
                //   return IntegrationsActions.connectDebtXSuccess({
                //     status: response.res.status,
                //   });
                // case 'DEBTX_WRONG_CREDENTIALS':
                //   return IntegrationsActions.connectDebtXWrongCredentials({
                //     status: response.res.status,
                //   });
                // case 'DEBTX_MFA_REQUIRED':
                //   return IntegrationsActions.connectDebtXMFA({
                //     mfaCode: response.res.mfaCode,
                // });
                case 'DEBTX_FAILURE':
                  return IntegrationsActions.connectDebtXFailure({
                    payload: response,
                  });
                default:
                  return IntegrationsActions.connectDebtXSuccess({
                    status: response.res.status,
                  });
              }
            }
          }),
          catchError(res =>
            of(IntegrationsActions.connectDebtXFailure({ payload: res }))
          )
        )
      )
    )
  );

  connectDebtXMfa$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.connectDebtXMFA),
      switchMap(({ type, ...payload }) =>
        this.integrationsService.connectDebtXMfa(payload.mfaCode).pipe(
          map(response => {
            if (response.sts && response.sts.sts === 'WARN') {
              return IntegrationsActions.connectDebtXFailure({
                payload: response,
              });
            } else {
              return IntegrationsActions.connectDebtXSuccess({
                status: response.res.status,
              });
            }
          }),
          catchError(res =>
            of(IntegrationsActions.connectDebtXFailure({ payload: res }))
          )
        )
      )
    )
  );

  getDebtXProfile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.getDebtXProfile),
      switchMap(() =>
        this.integrationsService.getDebtX().pipe(
          map(response =>
            IntegrationsActions.getDebtXProfileSuccess({
              status: response.res.status,
              data: response.res.data,
            })
          ),
          catchError(({ sts }) =>
            of(IntegrationsActions.getDebtXProfileFailure({ payload: sts }))
          )
        )
      )
    )
  );

  getIntercomToken$: Observable<Action> = createEffect(() =>
    toObservable(this.userStore.prs, { injector: this.injector }).pipe(
      filter(prs => !!prs?.prsId),
      distinctUntilChanged((prev, curr) => prev?.prsId === curr?.prsId),
      switchMap(prs => {
        const cached = sessionStorage.getItem('intercom_user_jwt');
        if (cached && prs?.usrId && isIntercomTokenValid(cached, prs.usrId)) {
          return of(
            IntegrationsActions.getIntercomTokenSuccess({ token: cached })
          );
        }
        return this.integrationsService.getIntercomToken().pipe(
          map(({ res }) => {
            const token = res?.intercomToken ?? null;
            if (token) {
              sessionStorage.setItem('intercom_user_jwt', token);
            }
            return IntegrationsActions.getIntercomTokenSuccess({ token });
          }),
          catchError(() => {
            console.warn('[Intercom] token request failed — chat unavailable');
            return of(IntegrationsActions.getIntercomTokenFailure());
          })
        );
      })
    )
  );
}
